import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit2,
  Trash2,
  RotateCcw,
  Send,
  Archive,
  EyeOff,
  Tag,
  Shield,
  Layers,
  Sparkles,
  RefreshCw,
  FileText,
} from 'lucide-react';
import Swal from 'sweetalert2';
import ArticleDetailModal from '../components/ArticleDetailModal';
import ArticleEditModal from '../components/ArticleEditModal';
import CategoryManagerModal from '../components/CategoryManagerModal';
import AuditLogsModal from '../components/AuditLogsModal';
import Pagination from '../components/Pagination';
import {
  getArticles,
  deleteArticle,
  restoreArticle,
  publishArticle,
  unpublishArticle,
  archiveArticle,
  getCategories,
} from '../../../services/newsNewsletterApi';

export default function ArticlesTab() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters & Search
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedEditorialStatus, setSelectedEditorialStatus] = useState('');
  const [selectedOrigin, setSelectedOrigin] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Pagination state (Server-side)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [kpis, setKpis] = useState({
    total: 0,
    pendingReviewCount: 0,
    publishedCount: 0,
    draftCount: 0,
    rejectedCount: 0,
  });

  // Modals
  const [detailArticle, setDetailArticle] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [auditArticleTarget, setAuditArticleTarget] = useState(null);

  // Debounce search input to avoid excessive API requests
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadArticles();
  }, [debouncedSearch, selectedCategory, selectedEditorialStatus, selectedOrigin, includeDeleted, page, limit]);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      if (res?.success) {
        setCategories(res.data || []);
      }
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        includeDeleted,
      };

      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (selectedCategory) params.categoryId = selectedCategory;
      if (selectedEditorialStatus) params.editorialStatus = selectedEditorialStatus;
      if (selectedOrigin) params.origin = selectedOrigin;

      const res = await getArticles(params);
      if (res?.success) {
        setArticles(res.data || []);
        const pg = res.pagination || {
          total: res.data?.length || 0,
          page,
          limit,
          totalPages: Math.ceil((res.data?.length || 0) / limit) || 1,
        };
        setPagination(pg);
        if (res.kpis) {
          setKpis(res.kpis);
        }
        // Auto-correct page if current page exceeds totalPages (e.g. after deletion)
        if (pg.totalPages > 0 && page > pg.totalPages) {
          setPage(pg.totalPages);
        }
      }
    } catch (err) {
      console.error('Erreur chargement articles:', err);
    } finally {
      setLoading(false);
    }
  };

  // Actions
  const handlePublish = async (article) => {
    const confirm = await Swal.fire({
      title: 'Publier cet article ?',
      text: `L'article "${article.title}" passera en statut PUBLISHED (visible pour les abonnés et la newsletter).`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, publier',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#38A169',
    });

    if (confirm.isConfirmed) {
      try {
        await publishArticle(article._id);
        Swal.fire('Publié', 'Article publié avec succès.', 'success');
        loadArticles();
      } catch (err) {
        Swal.fire('Erreur de publication', err.message || 'Impossible de publier cet article', 'error');
      }
    }
  };

  const handleUnpublish = async (article) => {
    const confirm = await Swal.fire({
      title: 'Dépublier cet article ?',
      text: `L'article "${article.title}" repassera en statut non publié (UNPUBLISHED).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Dépublier',
      cancelButtonText: 'Annuler',
    });

    if (confirm.isConfirmed) {
      try {
        await unpublishArticle(article._id);
        Swal.fire('Dépublié', 'Article dépublié.', 'success');
        loadArticles();
      } catch (err) {
        Swal.fire('Erreur', err.message, 'error');
      }
    }
  };

  const handleArchive = async (article) => {
    const confirm = await Swal.fire({
      title: 'Archiver cet article ?',
      text: `L'article "${article.title}" sera transféré dans les archives (ARCHIVED).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Archiver',
      cancelButtonText: 'Annuler',
    });

    if (confirm.isConfirmed) {
      try {
        await archiveArticle(article._id);
        Swal.fire('Archivé', 'Article archivé.', 'success');
        loadArticles();
      } catch (err) {
        Swal.fire('Erreur', err.message, 'error');
      }
    }
  };

  const handleDelete = async (article) => {
    const confirm = await Swal.fire({
      title: 'Supprimer cet article ?',
      text: `L'article "${article.title}" sera placé dans la corbeille logique (récupérable).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#E53E3E',
    });

    if (confirm.isConfirmed) {
      try {
        await deleteArticle(article._id);
        Swal.fire('Supprimé', 'Article supprimé logiquement.', 'success');
        loadArticles();
      } catch (err) {
        Swal.fire('Erreur', err.message, 'error');
      }
    }
  };

  const handleRestore = async (article) => {
    try {
      await restoreArticle(article._id);
      Swal.fire('Restauré', 'L’article a été restauré avec succès.', 'success');
      loadArticles();
    } catch (err) {
      Swal.fire('Erreur', err.message, 'error');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="an-tab-container animate-fade-in">
      {/* Top Banner Actions & KPI */}
      <div className="an-stats-row">
        <div className="an-stat-box">
          <div className="an-stat-icon-wrap blue">
            <FileText size={24} />
          </div>
          <div className="an-stat-info">
            <span className="an-stat-num">{pagination.total}</span>
            <span className="an-stat-desc">Total Articles ({includeDeleted ? 'avec corbeille' : 'actifs'})</span>
          </div>
        </div>

        <div className="an-stat-box">
          <div className="an-stat-icon-wrap green">
            <Send size={24} />
          </div>
          <div className="an-stat-info">
            <span className="an-stat-num">{kpis.publishedCount ?? 0}</span>
            <span className="an-stat-desc">Articles en ligne (Publiés)</span>
          </div>
        </div>

        <div className="an-stat-box">
          <div className="an-stat-icon-wrap orange">
            <Layers size={24} />
          </div>
          <div className="an-stat-info">
            <span className="an-stat-num">{kpis.draftCount ?? 0}</span>
            <span className="an-stat-desc">Brouillons en rédaction</span>
          </div>
        </div>

        <div className="an-stat-box">
          <div className="an-stat-icon-wrap purple">
            <Tag size={24} />
          </div>
          <div className="an-stat-info">
            <span className="an-stat-num">{categories.length}</span>
            <span className="an-stat-desc">Catégories thématiques</span>
          </div>
        </div>
      </div>

      {/* Action Header: Create Article & Manage Categories */}
      <div className="an-action-header-row mt-4">
        <div className="an-header-buttons-left">
          <button
            type="button"
            className="an-btn-primary"
            onClick={() => setEditArticle({})}
          >
            <Plus size={18} />
            <span>Créer un article manuel</span>
          </button>

          <button
            type="button"
            className="an-btn-secondary"
            onClick={() => setIsCategoryModalOpen(true)}
          >
            <Tag size={18} />
            <span>Gérer les catégories</span>
          </button>
        </div>

        <div className="an-header-deleted-toggle">
          <label className="an-switch-label">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked);
                setPage(1);
              }}
            />
            <span>Afficher la corbeille (supprimés)</span>
          </label>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="an-filter-bar mt-3">
        <div className="an-search-input-wrapper">
          <Search size={18} className="an-search-icon" />
          <input
            type="text"
            className="an-search-input"
            placeholder="Rechercher par titre, code ou extrait..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="an-filters-group">
          <select
            className="an-filter-select"
            value={selectedEditorialStatus}
            onChange={(e) => {
              setSelectedEditorialStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tous les statuts éditoriaux</option>
            <option value="DRAFT">Brouillon (DRAFT)</option>
            <option value="PUBLISHED">Publié (PUBLISHED)</option>
            <option value="UNPUBLISHED">Dépublié (UNPUBLISHED)</option>
            <option value="ARCHIVED">Archivé (ARCHIVED)</option>
          </select>

          <select
            className="an-filter-select"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            className="an-filter-select"
            value={selectedOrigin}
            onChange={(e) => {
              setSelectedOrigin(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Toutes les origines</option>
            <option value="MANUAL">Rédaction Manuelle (MANUAL)</option>
            <option value="AI_AGENT">Collecte Agent IA (AI_AGENT)</option>
          </select>

          <button
            type="button"
            className="an-btn-icon-refresh"
            onClick={loadArticles}
            title="Rafraîchir"
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Articles Table */}
      <div className="an-table-card mt-3">
        {loading ? (
          <div className="an-loading-state">Chargement des articles...</div>
        ) : articles.length === 0 ? (
          <div className="an-empty-state">
            <FileText size={48} className="text-muted mb-3" />
            <h3>Aucun article trouvé</h3>
            <p className="text-muted">
              Commencez par rédiger un nouvel article manuel ou approuvez une proposition de l'agent IA.
            </p>
          </div>
        ) : (
          <div className="an-table-responsive">
            <table className="an-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Visuel</th>
                  <th>Titre & Code</th>
                  <th>Catégorie</th>
                  <th>Origine</th>
                  <th>Statut Éditorial</th>
                  <th>Validation</th>
                  <th>Créé le</th>
                  <th>Publié le</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((item) => {
                  const isDeleted = Boolean(item.isDeleted);
                  return (
                    <tr key={item._id} className={isDeleted ? 'table-deleted-row' : ''}>
                      {/* Image Thumbnail */}
                      <td>
                        {item.coverImage?.url ? (
                          <img
                            src={item.coverImage.url}
                            alt=""
                            className="an-table-thumb"
                          />
                        ) : (
                          <div className="an-table-thumb-placeholder">
                            <FileText size={16} className="text-muted" />
                          </div>
                        )}
                      </td>

                      {/* Title & Code */}
                      <td>
                        <div className="an-table-title-cell">
                          <span className="an-code-badge">{item.articleCode}</span>
                          <span className="font-bold text-dark">{item.title}</span>
                          {isDeleted && <span className="an-badge-deleted">Corbeille</span>}
                        </div>
                      </td>

                      {/* Category */}
                      <td>
                        <span className="an-cat-tag">
                          {item.primaryCategoryId?.name || 'Général'}
                        </span>
                      </td>

                      {/* Origin */}
                      <td>
                        <span className="an-origin-text">
                          {item.origin === 'AI_AGENT' ? 'Agent IA' : 'Manuel'}
                        </span>
                      </td>

                      {/* Editorial Status */}
                      <td>
                        <span
                          className={`an-badge an-badge-status-${item.editorialStatus?.toLowerCase()}`}
                        >
                          {item.editorialStatus}
                        </span>
                      </td>

                      {/* Validation Status */}
                      <td>
                        <span
                          className={`an-badge an-badge-val-${item.validationStatus?.toLowerCase()}`}
                        >
                          {item.validationStatus}
                        </span>
                      </td>

                      {/* Created date */}
                      <td className="text-muted small">{formatDate(item.createdAt)}</td>

                      {/* Published date */}
                      <td className="small">
                        {item.publishedAt ? (
                          <span className="text-success font-medium">
                            {formatDate(item.publishedAt)}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="text-right">
                        <div className="an-table-actions">
                          {/* View Detail */}
                          <button
                            type="button"
                            className="an-action-btn view"
                            onClick={() => setDetailArticle(item)}
                            title="Aperçu complet"
                          >
                            <Eye size={15} />
                          </button>

                          {/* Edit */}
                          {!isDeleted && (
                            <button
                              type="button"
                              className="an-action-btn edit"
                              onClick={() => setEditArticle(item)}
                              title="Modifier l'article"
                            >
                              <Edit2 size={15} />
                            </button>
                          )}

                          {/* Publish */}
                          {!isDeleted &&
                            item.editorialStatus !== 'PUBLISHED' &&
                            item.validationStatus === 'APPROVED' && (
                              <button
                                type="button"
                                className="an-action-btn publish"
                                onClick={() => handlePublish(item)}
                                title="Publier l'article"
                              >
                                <Send size={15} />
                              </button>
                            )}

                          {/* Unpublish */}
                          {!isDeleted && item.editorialStatus === 'PUBLISHED' && (
                            <button
                              type="button"
                              className="an-action-btn unpublish"
                              onClick={() => handleUnpublish(item)}
                              title="Dépublier l'article"
                            >
                              <EyeOff size={15} />
                            </button>
                          )}

                          {/* Archive */}
                          {!isDeleted && item.editorialStatus !== 'ARCHIVED' && (
                            <button
                              type="button"
                              className="an-action-btn archive"
                              onClick={() => handleArchive(item)}
                              title="Archiver"
                            >
                              <Archive size={15} />
                            </button>
                          )}

                          {/* Soft Delete */}
                          {!isDeleted && (
                            <button
                              type="button"
                              className="an-action-btn delete"
                              onClick={() => handleDelete(item)}
                              title="Mettre à la corbeille"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}

                          {/* Restore */}
                          {isDeleted && (
                            <button
                              type="button"
                              className="an-action-btn restore"
                              onClick={() => handleRestore(item)}
                              title="Restaurer l'article"
                            >
                              <RotateCcw size={15} />
                            </button>
                          )}

                          {/* Audit Logs */}
                          <button
                            type="button"
                            className="an-action-btn audit"
                            onClick={() => setAuditArticleTarget(item)}
                            title="Journal d'audit"
                          >
                            <Shield size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Server-Side Pagination Bar */}
        {pagination.total > 0 && (
          <Pagination
            currentPage={page}
            totalPages={pagination.totalPages || 1}
            totalItems={pagination.total || 0}
            limit={limit}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
            itemLabel="articles"
            disabled={loading}
          />
        )}
      </div>

      {/* Modals */}
      {detailArticle && (
        <ArticleDetailModal
          isOpen={Boolean(detailArticle)}
          onClose={() => setDetailArticle(null)}
          article={detailArticle}
          onEdit={(art) => setEditArticle(art)}
        />
      )}

      {editArticle && (
        <ArticleEditModal
          isOpen={Boolean(editArticle)}
          onClose={() => setEditArticle(null)}
          article={editArticle._id ? editArticle : null}
          onSaved={() => loadArticles()}
        />
      )}

      {isCategoryModalOpen && (
        <CategoryManagerModal
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onCategoriesUpdated={() => {
            loadCategories();
            loadArticles();
          }}
        />
      )}

      {auditArticleTarget && (
        <AuditLogsModal
          isOpen={Boolean(auditArticleTarget)}
          onClose={() => setAuditArticleTarget(null)}
          article={auditArticleTarget}
        />
      )}
    </div>
  );
}
